import React from 'react';
import { Trans } from 'react-i18next';
import logo from '../../../assets/images/logo.png';
import { frenchDateOnly } from '../../../variables/constants';
import { tDocument } from '../../../i18n/documents';
import BlocSignature from '../../../components/BlocSignature';

const G = '#1a6b3c';

const HeaderDivider = () => (
    <div style={{ height: '2px', background: `linear-gradient(to right, ${G}, #86efac)`, margin: '8px 0' }} />
);

/* Lettre remise a la victime : dans SA langue (`langue`, celle de sa fiche), pas dans
   celle de l'agent. Le francais est celui d'avant, au caractere pres. */
export default function NoteVictime({ sinistre, affectation, immatriculation, signature = null, langue = 'fr' }) {
    const d = tDocument(langue);
    const today = new Date().toLocaleDateString(langue === 'en' ? 'en-GB' : 'fr-FR', {
        day: '2-digit', month: 'long', year: 'numeric',
    });

    const victimeNom = affectation?.victimeNomPrenom || '-';
    const numSinistre = sinistre?.numeroSinistreLocal || sinistre?.numSinistre || '-';
    const dateAccident = sinistre?.dateAccident ? frenchDateOnly(sinistre.dateAccident) : '-';
    const souscripteurNom = sinistre?.assuréNom || sinistre?.souscripteurNom || '';
    const nomExpert = affectation?.expertNomComplet || '-';
    const expertAdresse = affectation?.expertAdresse || '';
    const expertContact = affectation?.expertContact || '';
    /* Civilite inconnue (cas courant : le serveur n'en transmet pas) : « Monsieur/Madame »
       en francais, comme avant ; en anglais, on s'adresse a la personne (« Dear Sir or
       Madam »). Connue : Monsieur, Madame… en francais, Mr ou Ms en anglais. */
    const civiliteFiche = affectation?.victimeCivilite;
    const anonyme = !civiliteFiche || civiliteFiche === 'Monsieur/Madame' ? 'anonyme' : undefined;
    const civilite = anonyme ? '' : d(`noteVictime.civilites.${civiliteFiche}`, { defaultValue: civiliteFiche });
    // Expertise matérielle (automobile) : la note invite à présenter le véhicule, pas à une visite médicale.
    const isMat = affectation?.expertTypeLabel === 'AUTOMOBILE';

    return (
        <div style={{
            fontFamily: "Calibri, 'Calibri Light', Arial, sans-serif",
            /* Zone utile d'une A4 : 210 mm moins les 20 mm de marge de chaque cote,
               declarees par @page a l'impression. Le document etait dessine en pixels
               (800 px) et l'impression compensait par un zoom a 0,78 - chaque taille
               en points sortait donc reduite de 22 %. */
            width: '170mm',
            margin: '0 auto',
            padding: 0,
            lineHeight: 1.35,
            fontSize: '10pt',
            color: '#000000',
            background: '#ffffff',
        }}>

            {/* ── En-tête ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <div>
                    <div style={{ fontSize: '15pt', fontWeight: 'bold', color: G, letterSpacing: '-0.5px' }}>
                        CBConnect
                    </div>
                    <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: '2px' }}>
                        {d('noteVictime.sousTitre')}
                    </div>
                </div>
                <img src={logo} alt="Logo BNCB Togo" style={{ height: '52px', objectFit: 'contain' }} />
            </div>
            <HeaderDivider />

            {/* ── Référence ── */}
            <div style={{ marginTop: '4mm', marginBottom: '5mm' }}>
                <span style={{ fontSize: '10pt' }}>
                    <strong>{d('noteVictime.ref')}</strong> {numSinistre} / BNCB Togo
                </span>
            </div>

            {/* ── Bloc victime (droite) + date ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6mm' }}>
                <div style={{ textAlign: 'left', minWidth: '240px' }}>
                    <p style={{ margin: '0 0 2px 0', fontWeight: 'bold', fontSize: '11pt' }}>
                        {d('noteVictime.destinataire', { civilite, nom: victimeNom, context: anonyme })}
                    </p>
                    {affectation?.victimeContact && (
                        <p style={{ margin: '0 0 2px 0', fontSize: '10pt' }}>
                            <strong>{d('noteVictime.tel')}</strong> {affectation.victimeContact}
                        </p>
                    )}
                    <p style={{ margin: '0 0 2px 0', fontSize: '10pt' }}>
                        Lomé – TOGO
                    </p>
                    <div style={{ borderBottom: '1px solid #000', margin: '8px 0' }} />
                    <p style={{ margin: '6px 0 0 0', fontSize: '10pt' }}>
                        {d('noteVictime.lomeLe', { date: today })}
                    </p>
                </div>
            </div>

            {/* ── Objet ── */}
            <div style={{ marginBottom: '5mm' }}>
                <p style={{ margin: 0, fontSize: '10pt' }}>
                    <span style={{ textDecoration: 'underline', fontWeight: 'bold' }}>{d('noteVictime.objet')}</span>{' '}
                    <strong>
                        {d('noteVictime.objetSinistre', { numero: numSinistre })}
                        {souscripteurNom ? ` - ${souscripteurNom}` : ''}
                        {' '}{d('noteVictime.contre', { nom: victimeNom })}
                    </strong>
                </p>
                <p style={{ margin: '4px 0 0 52px', fontSize: '10pt' }}>
                    <strong>{d('noteVictime.accidentDu', { date: dateAccident })}</strong>
                </p>
            </div>

            {/* ── Corps ── */}
            <div style={{ textAlign: 'justify', fontSize: '10pt', lineHeight: 1.4 }}>
                <p style={{ marginBottom: '2.2mm' }}>
                    {d('noteVictime.appel', { civilite, nom: victimeNom, context: anonyme })}
                </p>

                <p style={{ marginBottom: '2.2mm' }}>
                    {d('noteVictime.intro')}
                </p>

                {isMat ? (
                    <>
                        <p style={{ marginBottom: '2.5mm' }}>
                            {immatriculation
                                ? <Trans t={d} i18nKey="noteVictime.materielImmatricule" values={{ immatriculation }} components={{ b: <strong /> }} />
                                : d('noteVictime.materiel')}
                        </p>
                        <ul style={{ margin: '0 0 3mm 0', paddingLeft: '8mm', fontSize: '10pt', lineHeight: 1.4 }}>
                            <li>{d('noteVictime.piecesMateriel.carteGrise')}</li>
                            <li>{d('noteVictime.piecesMateriel.attestation')}</li>
                            <li>{d('noteVictime.piecesMateriel.identite')}</li>
                        </ul>
                    </>
                ) : (
                    <>
                        <p style={{ marginBottom: '2.5mm' }}>
                            {d('noteVictime.medical')}
                        </p>
                        <ul style={{ margin: '0 0 3mm 0', paddingLeft: '8mm', fontSize: '10pt', lineHeight: 1.4 }}>
                            <li>{d('noteVictime.piecesMedical.certificat')}</li>
                            <li>{d('noteVictime.piecesMedical.ordonnances')}</li>
                            <li>{d('noteVictime.piecesMedical.radiographies')}</li>
                            <li>{d('noteVictime.piecesMedical.bilans')}</li>
                            <li>{d('noteVictime.piecesMedical.identite')}</li>
                        </ul>
                    </>
                )}

                {/* Bloc expert centré */}
                <div style={{
                    textAlign: 'center',
                    margin: '6mm 0',
                    padding: '3mm 4mm',
                    border: `1px solid ${G}44`,
                    borderRadius: '6px',
                    background: '#f0fdf4',
                }}>
                    <p style={{ margin: '0 0 4px 0', fontWeight: 'bold', fontSize: '11pt' }}>
                        {d(isMat ? 'noteVictime.expertMateriel' : 'noteVictime.expertMedical', { nom: nomExpert })}
                    </p>
                    {expertAdresse && (
                        <p style={{ margin: '0 0 4px 0', fontSize: '10pt' }}>
                            {expertAdresse}
                        </p>
                    )}
                    {expertContact && (
                        <p style={{ margin: '0 0 4px 0', fontSize: '10pt' }}>
                            <strong>{d('noteVictime.tel')}</strong> {expertContact}
                        </p>
                    )}
                    <p style={{ margin: '0', fontSize: '10pt' }}>
                        Lomé – Togo
                    </p>
                </div>

                <p style={{ marginBottom: '2.2mm' }}>
                    {d(isMat ? 'noteVictime.rendezVousMateriel' : 'noteVictime.rendezVousMedical')}
                </p>

                {!isMat && (
                    <p style={{ marginBottom: '2.2mm' }}>
                        {d('noteVictime.article244')}
                    </p>
                )}

                <p style={{ marginBottom: '2.2mm' }}>
                    {d('noteVictime.suite')}
                </p>

                <p style={{ marginBottom: '2.2mm' }}>
                    <Trans t={d} i18nKey="noteVictime.salutations" context={anonyme} values={{ civilite }} components={{ b: <strong /> }} />
                </p>
            </div>

            {/* ── Signature ── */}
            <div style={{ marginTop: '6mm', textAlign: 'right', marginBottom: '3mm' }}>
                <p style={{ margin: 0, fontWeight: 'bold', fontSize: '10pt', textDecoration: 'underline' }}>
                    {d('noteVictime.direction')}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: '10pt' }}>
                    BNCB Togo
                </p>
                {/* CE DOCUMENT NE RÉSERVE AUCUN ESPACE DE SIGNATURE : contrairement aux
                    attestations, la hauteur ajoutée ici s'ajoute vraiment. La mesure du
                    15/09 donnait 247 mm pour 261 mm utilisables, soit 14 mm de marge.
                    D'où 10 mm, qui en laisse 4. Augmenter cette valeur sans remesurer
                    ferait déborder sur une seconde page ne portant que le pied de page. */}
                <BlocSignature
                    codeDocument="NOTE_VICTIME"
                    sinistreTrackingId={sinistre?.sinistreTrackingId}
                    hauteurMm={10}
                    signature={signature}
                />
            </div>

            {/* ── Pied de page ── */}
            <div style={{
                marginTop: '6mm',
                paddingTop: '10px',
                borderTop: '1px solid #e5e7eb',
                fontSize: '9pt',
                color: '#9ca3af',
                textAlign: 'center',
            }}>
                {d('noteVictime.pied', { date: today })}
            </div>
        </div>
    );
}
